import React, {
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { I18nContext } from '@bingads-webui-react/i18n-context';
import { TextField } from '@bingads-webui-react/fabric-control';

import { FluentImagePicker } from '@bingads-webui-campaign-react/fluent-image-picker';
import { AssetLibrarySpinner } from '@bingads-webui-campaign-react/asset-library-spinner';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { isValidUrl } from '@bingads-webui/url-util';
import { PlaceholderView } from '@bingads-webui-campaign-react/add-website-images-view';
import _ from 'underscore';

import { getSocialMediaStyles } from './styles/index';

const twitterReg = new RegExp(/(http:\/\/|https:\/\/)?(www\.)?twitter.com\/(\w+)\/?/);

export const useSocialMediaRepositoryToSetImages = ({ socialMediaDataSource }) => {
  const [images, setImages] = useState([]);
  const [socialMediaAccount, setSocialMediaAccount] = useState({ Value: '' });

  socialMediaDataSource.subscribe((newStatus) => {
    if (newStatus === 'Done') {
      setImages([
        ...socialMediaDataSource.data.map(img => ({
          ...img,
          thumbnailSrc: img.src,
        })),
      ]);

      const newSocialMediaAccount = socialMediaDataSource.socialMediaAccounts[0];

      if (newSocialMediaAccount) {
        setSocialMediaAccount(newSocialMediaAccount);
      }
    }
  });

  return {
    images,
    socialMediaAccount,
  };
};

export const SocialImageView = ({
  images,
  fetchImages,
  fetchImageByAccount,
  socialMediaAccount,
  finalUrl,
  onUpdateFinalUrl,
  addImageByUrl,
  logger,
  onUpdateFinalUrlPrefix,
  finalUrlProtocol,
}) => {
  const showPlaceholder =
    useMemo(() => !finalUrl || !isValidUrl(finalUrl), [finalUrl]);
  const i18n = useContext(I18nContext);
  const [isLoading, setIsLoading] = useState(true);
  const [account, setAccount] = useState('');
  const [alertMessage, setAlertMessage] = useState('');
  const classes = useDefaultStyles(getSocialMediaStyles);

  const setAlertMessageByResult = useCallback((result) => {
    if (result === 'NoAccountFound') {
      setAlertMessage(i18n.getString(_TL_('No Twitter account found from your URL. Please enter your Twitter account URL manually')));
    } else if (result === 'NoImagesFound') {
      setAlertMessage(i18n.getString(_TL_('No images posted recently on your Twitter account.')));
    } else if (result === 'Done') {
      setAlertMessage(i18n.getString(_TL_('We\'ve found images from your Twitter account.')));
    }
  }, [setAlertMessage, i18n]);

  const loadSocialImages = useCallback((url) => {
    setIsLoading(true);

    fetchImages(url).then((result) => {
      setAlertMessageByResult(result);

      logger.logTrace('social media image fetched');
      setIsLoading(false);
    });
  }, [setIsLoading, fetchImages, setAlertMessageByResult, logger]);

  useEffect(() => {
    if (!showPlaceholder) {
      loadSocialImages(finalUrl);
    }
  }, []);

  useEffect(() => {
    setAccount(`https://twitter.com/${socialMediaAccount.Value}`);
  }, [socialMediaAccount.Value]);

  const onAccountChange = useCallback((ev, newValue) => {
    setAccount(newValue);
  }, [setAccount]);

  const onBlur = useCallback(() => {
    const result = account.match(twitterReg);

    /* istanbul ignore next */
    if (result && result[3]) {
      setAlertMessage('');
      setIsLoading(true);
      fetchImageByAccount([{ Key: 'Twitter', Value: result[3] }]).then((fetchResult) => {
        setAlertMessageByResult(fetchResult);
        setIsLoading(false);
      });
    } else {
      setAlertMessage(i18n.getString(_TL_('Please enter the correct Twitter URL, like: https://twitter.com/microsoft')));
    }
  }, [setAlertMessage, fetchImageByAccount, account, i18n, setAlertMessageByResult]);

  const onSelectImage = useCallback((index) => {
    const imageToBeSelected = images[index];

    logger.logTrace('social media image selected');

    addImageByUrl(imageToBeSelected.src);
  }, [images, addImageByUrl, logger]);

  const onEnteredFinalUrl = useCallback((newUrl, ev) => {
    onUpdateFinalUrl(ev);
    loadSocialImages(newUrl);
  }, [onUpdateFinalUrl, loadSocialImages]);

  const onKeyDown = useCallback((ev) => {
    if (ev.key === 'Enter') {
      onBlur(ev);
    }
  }, [onBlur]);

  return (
    <div className={classnames('social-image-view', classes.socialMediaRoot)}>
      {
        showPlaceholder &&
          <PlaceholderView
            onEnteredFinalUrl={onEnteredFinalUrl}
            protocol={finalUrlProtocol}
            onUpdateProtocol={onUpdateFinalUrlPrefix}
          />
      }
      {
        !showPlaceholder &&
        <div className={classnames(classes.socialMediaContainer, 'social-media-container')}>
          {
            !isLoading && <TextField
              label={alertMessage}
              value={account}
              iconProps={{ iconName: 'Search' }}
              className={classnames(classes.textField, 'desc', 'social-url')}
              onBlur={onBlur}
              onChange={onAccountChange}
              onKeyDown={onKeyDown}
              styles={{ fieldGroup: { width: 300 } }}
            />
          }
          {
            isLoading ? <AssetLibrarySpinner useCustomSize customStyle={{ marginTop: '50px' }} /> :
            <FluentImagePicker
              images={images}
              pageSize={30}
              loadMoreImages={_.noop}
              onSelectImage={onSelectImage}
              hasMoreImages={false}
            />
          }
        </div>
      }
    </div>
  );
};

SocialImageView.propTypes = {
  images: PropTypes.arrayOf(PropTypes.shape({
    thumbnailSrc: PropTypes.string,
  })).isRequired,
  fetchImages: PropTypes.func.isRequired,
  fetchImageByAccount: PropTypes.func.isRequired,
  socialMediaAccount: PropTypes.shape({
    Value: PropTypes.string,
  }),
  onUpdateFinalUrl: PropTypes.func.isRequired,
  addImageByUrl: PropTypes.func.isRequired,
  finalUrl: PropTypes.string,
  finalUrlProtocol: PropTypes.string,
  onUpdateFinalUrlPrefix: PropTypes.func,
  logger: PropTypes.shape({
    logTrace: PropTypes.func,
  }),
};

SocialImageView.defaultProps = {
  finalUrl: '',
  socialMediaAccount: {},
  logger: {
    logTrace: _.noop,
  },
  finalUrlProtocol: null,
  onUpdateFinalUrlPrefix: null,
};
